import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CompanyProfile } from '@core/domain-classes/company-profile';
import { Customer } from '@core/domain-classes/customer';
import { DeliveryMode } from '@core/domain-classes/deliverymode.enum';
import { SalesOrder } from '@core/domain-classes/sales-order';
import { SalesOrderItem } from '@core/domain-classes/sales-order-item';
import { ShipsTo, ShipsToAddressTypeEnum } from '@core/domain-classes/shipTo';
import { SecurityService } from '@core/security/security.service';
import { environment } from '@environments/environment';

export interface SalesOrderInvoiceView {
  order: SalesOrder;
  items: SalesOrderItem[];
  returnItems: SalesOrderItem[];
}

@Component({
  selector: 'app-sales-order-invoice',
  templateUrl: './sales-order-invoice.component.html',
  styleUrls: ['./sales-order-invoice.component.scss'],
})
export class SalesOrderInvoiceComponent implements OnInit, OnChanges {
  DeliveryMode = DeliveryMode;
  @Input() salesOrder: SalesOrder;
  @Input() salesOrders: SalesOrder[];
  @Output() printComplete = new EventEmitter<void>();

  invoiceList: SalesOrderInvoiceView[] = [];
  companyProfile: CompanyProfile;
  imageBaseUrl = environment.imageUrl;

  constructor(private securityService: SecurityService) {}

  ngOnInit(): void {
    this.subScribeCompanyProfile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['salesOrders'] && this.salesOrders?.length) {
      this.invoiceList = this.salesOrders.map((so) => this.buildInvoiceData(so));
      setTimeout(() => this.printInvoice(), 1000);
      return;
    }

    if (changes['salesOrder'] && this.salesOrder) {
      this.invoiceList = [this.buildInvoiceData(this.salesOrder)];
      this.salesOrder = null;
      setTimeout(() => this.printInvoice(), 1000);
    }
  }

  private buildInvoiceData(order: SalesOrder): SalesOrderInvoiceView {
    const salesOrder = { ...order };
    salesOrder.totalQuantity = salesOrder.salesOrderItems
      .filter((item) => {
        const isSpecial =
          item.productName?.toLowerCase().includes('discount') ||
          item.productName?.toLowerCase().includes('upcharge');
        return !isSpecial;
      })
      .map((item) => (item.status == 0 ? item.quantity : -1 * item.quantity))
      .reduce((prev, next) => prev + next, 0);

    const items = salesOrder.salesOrderItems
      .filter((c) => c.status == 0)
      .map((item) => ({
        ...item,
        displayQuantity: item.quantity,
      }));

    const returnItems = salesOrder.salesOrderItems
      .filter((c) => c.status == 1)
      .map((item) => ({
        ...item,
        displayQuantity: item.quantity,
      }));

    return { order: salesOrder, items, returnItems };
  }

  subScribeCompanyProfile() {
    this.securityService.companyProfile.subscribe((data) => {
      this.companyProfile = data;
    });
  }

  getProductImageUrl(item: SalesOrderItem): string | null {
    const url = item?.product?.productUrl;
    if (!url) {
      return null;
    }
    if (url.startsWith('http')) {
      return url;
    }
    return `${this.imageBaseUrl}${url}`;
  }

  getCompanyLogoUrl(): string {
    if (this.companyProfile?.logoUrl) {
      return this.companyProfile.logoUrl.replace(/\\/g, '/');
    }
    return `${window.location.origin}/assets/images/ez-logo.png`;
  }

  getCustomerAddress(customer: Customer): string {
    if (!customer) {
      return '';
    }
    return [
      customer.address,
      customer.address2,
      customer.city?.cityName || customer.cityName,
      customer.state?.stateName,
      customer.postalCode,
    ]
      .filter((part) => !!part)
      .join(', ');
  }

  getCustomerPhone(customer: Customer): string {
    return customer?.phoneNo || customer?.contactPhoneNo || '';
  }

  getCustomerFax(customer: Customer): string {
    return customer?.fax || '';
  }

  getCustomerEmail(customer: Customer): string {
    return customer?.contactEmail || customer?.email || '';
  }

  private getOrderAddresses(order: SalesOrder): ShipsTo[] {
    return order?.addresses || (order as any)?.Addresses || [];
  }

  private getAddressByType(order: SalesOrder, type: ShipsToAddressTypeEnum): ShipsTo | null {
    const addresses = this.getOrderAddresses(order);
    return addresses.find((address) => address.adddressType === type) || null;
  }

  getBillToAddress(order: SalesOrder): ShipsTo | null {
    return this.getAddressByType(order, ShipsToAddressTypeEnum.Billing);
  }

  getShipToAddress(order: SalesOrder): ShipsTo | null {
    return this.getAddressByType(order, ShipsToAddressTypeEnum.Shipping);
  }

  getAddressDisplayLine(address: ShipsTo | null): string {
    if (!address) {
      return '';
    }

    return [
      address.address1,
      address.address2,
      address.city?.cityName,
      address.stateProvince?.stateName || address.stateProvince?.code,
      address.postalCode,
      address.country?.countryName,
    ]
      .filter((part) => !!part)
      .join(', ');
  }

  getBillToName(order: SalesOrder): string {
    const billTo = this.getBillToAddress(order);
    return billTo?.name || order?.customer?.customerName || '';
  }

  getBillToAddressLine(order: SalesOrder): string {
    const billTo = this.getBillToAddress(order);
    if (billTo) {
      return this.getAddressDisplayLine(billTo);
    }

    return this.getCustomerAddress(order?.customer);
  }

  getBillToPhone(order: SalesOrder): string {
    const billTo = this.getBillToAddress(order);
    return billTo?.phone || this.getCustomerPhone(order?.customer);
  }

  getBillToFax(order: SalesOrder): string {
    const billTo = this.getBillToAddress(order);
    return billTo?.fax || this.getCustomerFax(order?.customer);
  }

  getBillToEmail(order: SalesOrder): string {
    const billTo = this.getBillToAddress(order);
    return billTo?.email || this.getCustomerEmail(order?.customer);
  }

  getShipToName(order: SalesOrder): string {
    const shipTo = this.getShipToAddress(order);
    return shipTo?.name || order?.customer?.shipToName || order?.customer?.customerName || '';
  }

  getShipToAddressLine(order: SalesOrder): string {
    const shipTo = this.getShipToAddress(order);
    if (shipTo) {
      return this.getAddressDisplayLine(shipTo);
    }

    const customer = order?.customer;
    if (!customer) {
      return '';
    }

    return [
      customer.shipToAddress1,
      customer.shipToAddress2,
      customer.shipToCity?.cityName,
      customer.shipToState?.stateName,
      customer.shipToPostalCode,
      customer.shipToCountry?.countryName,
    ]
      .filter((part) => !!part)
      .join(', ');
  }

  getShipToPhone(order: SalesOrder): string {
    const shipTo = this.getShipToAddress(order);
    return shipTo?.phone || order?.customer?.shipToPhone || '';
  }

  getShipToFax(order: SalesOrder): string {
    const shipTo = this.getShipToAddress(order);
    return shipTo?.fax || order?.customer?.shipToFax || '';
  }

  getShipToEmail(order: SalesOrder): string {
    const shipTo = this.getShipToAddress(order);
    return shipTo?.email || order?.customer?.shipToEmail || '';
  }

  printInvoice() {
    const printContents = document.getElementById('salesOrderBulkPrint')?.innerHTML;
    if (!printContents) {
      return;
    }

    const title =
      this.invoiceList.length === 1
        ? this.invoiceList[0].order.orderNumber
        : `Sales Orders (${this.invoiceList.length})`;

    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    popupWin.document.open();
    popupWin.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
            @page { size: auto;  margin: 0mm;  margin-top:60px; }

            @media print {
              * {
                font-family: "Hind-Vadodara", sans-serif;
                -webkit-print-color-adjust: exact;
              }
              .sales-order-invoice-page {
                page-break-after: always;
                break-after: page;
              }
              .sales-order-invoice-page:last-child {
                page-break-after: auto;
                break-after: auto;
              }
            }
            tr{
              border: 1px solid #dee2e6;
              border-spacing: 2px;
            }
            table {
              border-collapse: collapse;
            }
            th, td {
              padding: 5px;
              border: 1px solid #dee2e6;
            }
            </style>
            <script>
            function loadHandler(){

            var is_chrome = function () { return Boolean(window.chrome); }
        if(is_chrome)
        {
           window.print();
           setTimeout(function(){window.close();}, 1000);
        }
        else
        {
           window.print();
           window.close();
        }
        }
        </script>
          </head>
      <body onload="loadHandler()">${printContents}</body>
        </html>
    `);
    popupWin.document.close();
    this.printComplete.emit();
  }
}
